import {
  useAsyncOperation,
  useImportInvoicePreview,
  useSiteInvoicePreview,
  useSiteOperationalActions,
} from "@inrange/building-manager-api-client";
import {
  currencySymbol,
  formatCurrency,
  formatUnits,
  fractionalCurrencySymbol,
} from "@inrange/theme-components/formatting";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useQueryClient } from "@tanstack/react-query";
import Loading from "../../Loading";

import { formatDateYMD } from "../siteSections/utils";

const InvoicePreviewModal = ({
  siteId,
  month,
  currencyCode,
  initialInvoiceDate,
  onClose,
  isImportInvoice = false,
}: {
  siteId: string;
  month: string;
  currencyCode: string;
  initialInvoiceDate: Date;
  onClose: () => void;
  isImportInvoice?: boolean;
}) => {
  const { fetchInvoicePreview } = useSiteInvoicePreview({
    siteId,
    month,
    enabled: !isImportInvoice,
  });
  const { fetchImportInvoicePreview } = useImportInvoicePreview({
    siteId,
    month,
    enabled: isImportInvoice,
  });
  const { processBilling, processImportInvoice } = useSiteOperationalActions();
  const queryClient = useQueryClient();
  const [otherChargeItems, setOtherChargeItems] = useState<
    Array<{
      description: string;
      amount: string;
      type: "charge" | "consumption_adjustment";
    }>
  >([]);
  const [invoiceDate, setInvoiceDate] = useState<Date>(initialInvoiceDate);
  const [operationKey, setOperationKey] = useState<string | null>(null);
  const [submittingFormat, setSubmittingFormat] = useState<
    "pdf" | "docx" | null
  >(null);

  const asyncOperation = useAsyncOperation({
    siteId,
    operationKey,
  });

  useEffect(() => {
    if (!asyncOperation.data) return;

    if (asyncOperation.data.status === "completed") {
      setOperationKey(null);
      setSubmittingFormat(null);
      queryClient.invalidateQueries(["site"]);
      onClose();
    } else if (asyncOperation.data.status === "failed") {
      setOperationKey(null);
      setSubmittingFormat(null);
      alert(
        `Error generating invoice: ${asyncOperation.data.error || "Unknown error"}`
      );
    }
  }, [asyncOperation.data, queryClient, onClose]);

  useEffect(() => {
    if (asyncOperation.timedOut) {
      setOperationKey(null);
      setSubmittingFormat(null);
      alert("Invoice generation timed out after 15 minutes.");
    }
  }, [asyncOperation.timedOut]);

  const invoiceData = fetchInvoicePreview.data;
  const importInvoiceData = fetchImportInvoicePreview.data;
  const isOO = !!invoiceData?.isOO;

  const importCalculations = useMemo(() => {
    if (!importInvoiceData) return null;

    const chargeItems = otherChargeItems.filter(
      (item) => item.type === "charge"
    );
    const otherCharges =
      Math.round(
        chargeItems.reduce(
          (sum, item) => sum + (parseFloat(item.amount) || 0),
          0
        ) * 100
      ) / 100;

    const electricalPaymentAmount = importInvoiceData.electricalPaymentAmount;
    const subtotal =
      Math.round((electricalPaymentAmount + otherCharges) * 100) / 100;
    const vatAmount =
      Math.round(subtotal * importInvoiceData.vatRate * 100) / 100;
    const total = Math.round((subtotal + vatAmount) * 100) / 100;

    return {
      electricityPrice: importInvoiceData.electricityPrice,
      importMatchReadings: importInvoiceData.importMatchReadings,
      totalElectricalOutputKwh: importInvoiceData.totalElectricalOutputKwh,
      electricalPaymentAmount,
      vatRate: importInvoiceData.vatRate,
      otherCharges,
      subtotal,
      vatAmount,
      total,
    };
  }, [importInvoiceData, otherChargeItems]);

  const calculations = useMemo(() => {
    if (!invoiceData) return null;

    const tariffRate = invoiceData.onSiteTariffRate || 0;
    const vatRate = invoiceData.vatRate || 0;

    const chargeItems = otherChargeItems.filter(
      (item) => item.type === "charge"
    );
    const adjustmentItems = otherChargeItems.filter(
      (item) => item.type === "consumption_adjustment"
    );

    const adjustmentKwh = adjustmentItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const consumption =
      Math.round(((invoiceData.consumptionKwh || 0) + adjustmentKwh) * 1000) /
      1000;
    const consumptionCharges = Math.round(consumption * tariffRate * 100) / 100;

    const otherCharges =
      Math.round(
        chargeItems.reduce(
          (sum, item) => sum + (parseFloat(item.amount) || 0),
          0
        ) * 100
      ) / 100;

    // OO revshare is a percentage of (consumptionCharges + spill_revenue);
    // when consumption is adjusted we have to recompute it locally - the
    // backend's invoiceData.ooRevenueShareAmount is based on the un-adjusted
    // consumption. Mirrors apply_invoice_adjustments() in the BMA.
    const ooRevenueShareAmount = (() => {
      if (!invoiceData.isOO) return 0;
      if (adjustmentItems.length === 0) {
        return invoiceData.ooRevenueShareAmount || 0;
      }
      const gridExportKwh = invoiceData.gridExportKwh || 0;
      const spillTariffRate = invoiceData.spillTariffRate || 0;
      const ooRevenueSharePct = invoiceData.ooRevenueSharePct || 0;
      const spillRevenue =
        Math.round(gridExportKwh * spillTariffRate * 100) / 100;
      const ooTotalRevenue =
        Math.round((consumptionCharges + spillRevenue) * 100) / 100;
      return Math.round(-1 * ooTotalRevenue * ooRevenueSharePct * 100) / 100;
    })();
    const baseSubtotal = invoiceData.isOO
      ? consumptionCharges + ooRevenueShareAmount + otherCharges
      : consumptionCharges + otherCharges;
    const subtotal = Math.round(baseSubtotal * 100) / 100;
    const vatAmount = Math.round(subtotal * vatRate * 100) / 100;
    const total = Math.round((subtotal + vatAmount) * 100) / 100;

    return {
      generatedKwh: invoiceData.generatedKwh || 0,
      consumption,
      tariffRate,
      vatRate,
      consumptionCharges,
      ooRevenueSharePct: invoiceData.ooRevenueSharePct || 0,
      ooRevenueShareAmount,
      spillTariffRate: invoiceData.spillTariffRate || 0,
      otherCharges,
      subtotal,
      vatAmount,
      total,
    };
  }, [invoiceData, otherChargeItems]);

  const getOtherChargeItemsForApi = () => {
    const items = otherChargeItems
      .filter((item) => item.description.trim() && parseFloat(item.amount))
      .map((item) => ({
        description: item.description.trim(),
        amount: parseFloat(item.amount),
        type: item.type,
      }));
    return items.length > 0 ? items : undefined;
  };

  const handleSubmit = async (format: "pdf" | "docx") => {
    if (isImportInvoice && !importCalculations) return;
    if (!isImportInvoice && !calculations) return;
    setSubmittingFormat(format);
    try {
      if (isImportInvoice) {
        const response = await processImportInvoice.mutateAsync({
          siteId,
          month,
          invoiceFormat: format,
          invoiceDate: formatDateYMD(invoiceDate),
          otherChargeItems: getOtherChargeItemsForApi(),
        });
        setOperationKey(response.operationKey);
      } else {
        const response = await processBilling.mutateAsync({
          siteId,
          readingAction: `month:${month}`,
          readingDbInsert: true,
          readingDbUpdate: true,
          attachReadings: true,
          invoiceFormat: format,
          invoiceDate: formatDateYMD(invoiceDate),
          otherChargeItems: getOtherChargeItemsForApi(),
        });
        setOperationKey(response.operationKey);
      }
    } catch (e) {
      setSubmittingFormat(null);
      alert(`Error starting invoice generation: ${e}`);
    }
  };

  const isSubmitting = !!submittingFormat;

  const statusMessage =
    submittingFormat && asyncOperation.data?.status === "running"
      ? isImportInvoice
        ? "Generating invoice..."
        : "Processing readings and generating invoice..."
      : submittingFormat
        ? "Starting operation..."
        : null;

  return (
    <Modal show={true} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Invoice - {month}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isImportInvoice && fetchInvoicePreview.isLoading && (
          <Loading label="Loading invoice data..." height="20vh" />
        )}
        {!isImportInvoice && fetchInvoicePreview.isError && (
          <div className="alert alert-danger">Error loading invoice data.</div>
        )}
        {isImportInvoice && fetchImportInvoicePreview.isLoading && (
          <Loading label="Loading import invoice data..." height="20vh" />
        )}
        {isImportInvoice && fetchImportInvoicePreview.isError && (
          <div className="alert alert-danger">
            Error loading import invoice data.
          </div>
        )}
        {statusMessage && (
          <Alert variant="info">
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            {statusMessage}
          </Alert>
        )}
        {((isImportInvoice && importCalculations) ||
          (!isImportInvoice && calculations && invoiceData)) && (
          <div>
            {(invoiceData?.siteName || importInvoiceData?.siteName) && (
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "1.1em",
                  marginBottom: "12px",
                }}
              >
                {isImportInvoice ? "Import invoice" : "Monthly invoice"} for{" "}
                {invoiceData?.siteName || importInvoiceData?.siteName}
              </div>
            )}
            <Row className="mb-3">
              <Col sm="auto">
                <Form.Group controlId="modalInvoiceDate">
                  <Form.Label>Invoice date</Form.Label>
                  <div>
                    <DatePicker
                      selected={invoiceDate}
                      onChange={(date: Date | null) => {
                        if (date) setInvoiceDate(date);
                      }}
                      dateFormat="dd MMM yyyy"
                      calendarStartDay={1}
                      disabled={isSubmitting}
                      className="form-control form-control-sm"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            {!isImportInvoice && calculations && (
              <>
                <h5 className="mb-3">Charges summary</h5>
                <Table borderless size="sm" responsive>
                  <tbody>
                    {isOO && (
                      <tr>
                        <td colSpan={2}>
                          <strong>Total electricity generated</strong>
                        </td>
                        <td className="text-end">
                          <strong>
                            {formatUnits(calculations.generatedKwh, "kWh", 3)}
                          </strong>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={2}>
                        <strong>
                          Total electricity consumed @{" "}
                          {(calculations.tariffRate * 100).toFixed(2)}
                          {fractionalCurrencySymbol(currencyCode)}/kWh
                        </strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatUnits(calculations.consumption, "kWh", 3)}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        Consumption charges
                        {isOO && (
                          <>
                            <br />
                            <span style={{ fontSize: "0.85em", color: "#666" }}>
                              (on-site consumption x on-site tariff)
                            </span>
                          </>
                        )}
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          calculations.consumptionCharges,
                          currencyCode,
                          2
                        )}
                      </td>
                    </tr>
                    {isOO && (
                      <tr>
                        <td colSpan={2}>
                          {(calculations.ooRevenueSharePct * 100).toFixed(0)}%
                          Revenue share
                          <br />
                          <span style={{ fontSize: "0.85em", color: "#666" }}>
                            (of consumption charges + export x{" "}
                            {(calculations.spillTariffRate * 100).toFixed(2)}
                            {fractionalCurrencySymbol(currencyCode)}/kWh)
                          </span>
                        </td>
                        <td className="text-end">
                          {formatCurrency(
                            calculations.ooRevenueShareAmount,
                            currencyCode,
                            2
                          )}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={2}>Other charges and adjustments</td>
                      <td className="text-end">
                        {formatCurrency(
                          calculations.otherCharges,
                          currencyCode,
                          2
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <strong>Subtotal of charges before VAT</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatCurrency(
                            calculations.subtotal,
                            currencyCode,
                            2
                          )}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        VAT @ {(calculations.vatRate * 100).toFixed(2)}%
                        (Standard Rated)
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          calculations.vatAmount,
                          currencyCode,
                          2
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <strong>Total amount payable</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatCurrency(calculations.total, currencyCode, 2)}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}
            {isImportInvoice && importCalculations && (
              <>
                <h5 className="mb-3">Electrical payment summary</h5>
                <Table borderless size="sm" responsive>
                  <tbody>
                    <tr>
                      <td colSpan={2}>
                        <strong>Electricity price</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {(importCalculations.electricityPrice * 100).toFixed(
                            2
                          )}
                          {fractionalCurrencySymbol(currencyCode)}/kWh
                        </strong>
                      </td>
                    </tr>
                    {importCalculations.importMatchReadings.map((match, i) => (
                      <tr key={i}>
                        <td colSpan={2}>
                          Aggregate electrical output MPAN{" "}
                          {match.exportGridConnectionId}
                          <br />
                          <span style={{ fontSize: "0.85em", color: "#666" }}>
                            (actual reading of metering equipment)
                          </span>
                        </td>
                        <td className="text-end">
                          <strong>
                            {formatUnits(match.totalMeteredKwh, "kWh", 2)}
                          </strong>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        Electrical payment amount
                        <br />
                        <span style={{ fontSize: "0.85em", color: "#666" }}>
                          (electricity price x total electrical output)
                        </span>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatCurrency(
                            importCalculations.electricalPaymentAmount,
                            currencyCode,
                            2
                          )}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>Other charges and adjustments</td>
                      <td className="text-end">
                        {formatCurrency(
                          importCalculations.otherCharges,
                          currencyCode,
                          2
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <strong>Subtotal of charges before VAT</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatCurrency(
                            importCalculations.subtotal,
                            currencyCode,
                            2
                          )}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        VAT @ {(importCalculations.vatRate * 100).toFixed(2)}%
                        (Standard Rated)
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          importCalculations.vatAmount,
                          currencyCode,
                          2
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <hr />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <strong>Total payment due</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatCurrency(
                            importCalculations.total,
                            currencyCode,
                            2
                          )}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}

            <h5 className="mt-4 mb-3">Other charges and adjustments</h5>
            {otherChargeItems.length === 0 ? (
              <div style={{ color: "#666", marginBottom: "8px" }}>None</div>
            ) : (
              otherChargeItems.map((item, index) => (
                <div
                  key={index}
                  className="d-flex gap-2 mb-1 align-items-center"
                >
                  <Form.Control
                    type="text"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => {
                      const updated = [...otherChargeItems];
                      updated[index] = {
                        ...updated[index],
                        description: e.target.value,
                      };
                      setOtherChargeItems(updated);
                    }}
                    size="sm"
                    disabled={isSubmitting}
                  />
                  <Form.Control
                    type="text"
                    placeholder={
                      item.type === "consumption_adjustment"
                        ? "kWh"
                        : `Amount (${currencySymbol(currencyCode)})`
                    }
                    value={item.amount}
                    onChange={(e) => {
                      const updated = [...otherChargeItems];
                      updated[index] = {
                        ...updated[index],
                        amount: e.target.value,
                      };
                      setOtherChargeItems(updated);
                    }}
                    style={{ width: "120px" }}
                    size="sm"
                    disabled={isSubmitting}
                  />
                  {!isImportInvoice && (
                    <Form.Select
                      value={item.type}
                      onChange={(e) => {
                        const updated = [...otherChargeItems];
                        updated[index] = {
                          ...updated[index],
                          type: e.target.value as
                            | "charge"
                            | "consumption_adjustment",
                        };
                        setOtherChargeItems(updated);
                      }}
                      style={{ width: "180px" }}
                      size="sm"
                      disabled={isSubmitting}
                    >
                      <option value="charge">
                        Charge ({currencySymbol(currencyCode)})
                      </option>
                      <option value="consumption_adjustment">
                        Consumption adj. (kWh)
                      </option>
                    </Form.Select>
                  )}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() =>
                      setOtherChargeItems(
                        otherChargeItems.filter((_, i) => i !== index)
                      )
                    }
                    disabled={isSubmitting}
                  >
                    x
                  </Button>
                </div>
              ))
            )}
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() =>
                setOtherChargeItems([
                  ...otherChargeItems,
                  { description: "", amount: "", type: "charge" },
                ])
              }
              disabled={isSubmitting}
            >
              + Add line item
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={() => handleSubmit("pdf")}
          disabled={
            isSubmitting ||
            (isImportInvoice ? !importCalculations : !calculations)
          }
        >
          {submittingFormat === "pdf" ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Creating...
            </>
          ) : (
            "Attach PDF invoice"
          )}
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => handleSubmit("docx")}
          disabled={
            isSubmitting ||
            (isImportInvoice ? !importCalculations : !calculations)
          }
        >
          {submittingFormat === "docx" ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Creating...
            </>
          ) : (
            "Attach DOCX invoice"
          )}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoicePreviewModal;
